import { cloneDeep } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import TableColumnTool from './tableTool';
import { getShadowRoot, useBlock, useFocusIdx } from '@bay-email/editor';
import { TableSelectionProvider } from './TableSelectionContext';

export function TableOperation({ children }: { children?: React.ReactNode }) {
  const shadowRoot = getShadowRoot();
  const { focusIdx } = useFocusIdx();
  const { focusBlock, change } = useBlock();
  const topRef = useRef(null);
  const bottomRef = useRef(null);
  const leftRef = useRef(null);
  const rightRef = useRef(null);
  const overlayRef = useRef(null);
  const dotRef = useRef(null);
  const tool = useRef<TableColumnTool>();
  const [hasSelection, setHasSelection] = useState(false);
  const [canMerge, setCanMerge] = useState(false);

  useEffect(() => {
    const borderTool: any = {
      top: topRef.current,
      bottom: bottomRef.current,
      left: leftRef.current,
      right: rightRef.current,
      overlay: overlayRef.current,
      dot: dotRef.current,
    };
    // Attach to <body> if present, otherwise attach to the shadow root itself
    const attachRoot = (shadowRoot.querySelector('body') as any) || (shadowRoot as any);
    tool.current = new TableColumnTool(borderTool, attachRoot);
    return () => {
      tool.current?.destroy();
    };
  }, [shadowRoot]);

  useEffect(() => {
    if (tool.current) {
      tool.current.onSelectionChange = (sel, merge) => {
        setHasSelection(sel);
        setCanMerge(merge);
      };
      tool.current.changeTableData = (data: any[][]) => {
        change(`${focusIdx}.data.value.tableSource`, cloneDeep(data));
      };
      tool.current.tableData = cloneDeep(focusBlock?.data?.value?.tableSource || []);
    }
  }, [focusIdx, focusBlock]);

  // Table operation handlers
  const mergeCells = useCallback(() => tool.current?.mergeCells(), []);
  const deleteSelectedRows = useCallback(() => tool.current?.deleteSelectedRows(), []);
  const deleteSelectedColumns = useCallback(() => tool.current?.deleteSelectedColumns(), []);
  const insertRowUp = useCallback(() => tool.current?.insertRowUp(), []);
  const insertRowDown = useCallback(() => tool.current?.insertRowDown(), []);
  const insertColumnLeft = useCallback(() => tool.current?.insertColumnLeft(), []);
  const insertColumnRight = useCallback(() => tool.current?.insertColumnRight(), []);

  return (
    <TableSelectionProvider value={{ hasSelection, canMerge, mergeCells, deleteSelectedRows, deleteSelectedColumns, insertRowUp, insertRowDown, insertColumnLeft, insertColumnRight }}>
      {children}
      {createPortal(
        <div>
          <div ref={overlayRef} />
          <div ref={topRef} />
          <div ref={bottomRef} />
          <div ref={leftRef} />
          <div ref={rightRef} />
          <div ref={dotRef} />
        </div>,
        document.body,
      )}
    </TableSelectionProvider>
  );
}
